import React, { useState, useEffect } from 'react';
import { useToast } from '../Toast';
import Icon from '../Icon';
import { C, S } from '../../utils/styles';

export default function TaxSnapshotDetailModal({ snapshotId, onClose }) {
  const addToast = useToast();
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [applying, setApplying] = useState(false);
  const [ignoring, setIgnoring] = useState(false);

  useEffect(() => {
    fetchSnapshot();
  }, [snapshotId]);

  const fetchSnapshot = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/tax-api/snapshots/${snapshotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSnapshot(data);
      } else {
        addToast('Failed to fetch snapshot details', 'error');
        onClose();
      }
    } catch (err) {
      console.error('Failed to fetch snapshot:', err);
      addToast('Failed to fetch snapshot details', 'error');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/tax-api/apply/${snapshotId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        addToast(`✓ ${result.message}`, 'success');
        onClose();
      } else {
        const error = await response.json();
        addToast(error.detail || 'Failed to apply changes', 'error');
      }
    } catch (err) {
      console.error('Failed to apply snapshot:', err);
      addToast('Failed to apply tax rate changes', 'error');
    } finally {
      setApplying(false);
    }
  };

  const handleIgnore = async () => {
    setIgnoring(true);
    try {
      const token = localStorage.getItem('healthcues_token');
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/tax-api/snapshots/${snapshotId}/ignore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        addToast('Snapshot ignored successfully', 'success');
        onClose();
      } else {
        const error = await response.json();
        addToast(error.detail || 'Failed to ignore snapshot', 'error');
      }
    } catch (err) {
      console.error('Failed to ignore snapshot:', err);
      addToast('Failed to ignore snapshot', 'error');
    } finally {
      setIgnoring(false);
    }
  };

  const copyRawPayload = () => {
    if (snapshot?.rawPayload) {
      navigator.clipboard.writeText(JSON.stringify(snapshot.rawPayload, null, 2));
      addToast('Raw payload copied to clipboard', 'success');
    }
  };

  const statusBadge = (status) => {
    const badges = {
      applied: { bg: 'rgb(220, 252, 231)', color: 'rgb(22, 163, 74)', text: 'Applied' },
      pending_review: { bg: 'rgb(254, 243, 199)', color: 'rgb(217, 119, 6)', text: 'Pending Review' },
      ignored: { bg: 'rgb(241, 245, 249)', color: 'rgb(100, 116, 139)', text: 'Ignored' },
      failed: { bg: 'rgb(254, 226, 226)', color: 'rgb(220, 38, 38)', text: 'Failed' }
    };
    const badge = badges[status] || badges.pending_review;

    return (
      <span style={{
        padding: '6px 12px',
        borderRadius: 8,
        background: badge.bg,
        color: badge.color,
        fontSize: 13,
        fontWeight: 600
      }}>
        {badge.text}
      </span>
    );
  };

  return (
    <div
      data-testid="tax-sync-snapshot-detail-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
        padding: 24
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'white',
        borderRadius: 14,
        boxShadow: '0 10px 30px rgba(2, 6, 23, 0.15)',
        maxWidth: 1200,
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ ...S.heading, margin: 0 }}>Snapshot Details</h2>
            {snapshot && (
              <p style={{ fontSize: 13, color: C.textMid, marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>
                {snapshot._id}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              padding: 8,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: C.textMid,
              borderRadius: 8
            }}
          >
            <Icon name="close-outline" style={{ fontSize: 24 }} />
          </button>
        </div>

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
            <Icon name="sync-outline" style={{ fontSize: 32, color: C.accent, animation: 'spin 1s linear infinite' }} />
          </div>
        ) : snapshot ? (
          <>
            {/* Tabs */}
            <div style={{
              display: 'flex',
              borderBottom: `1px solid ${C.border}`,
              padding: '0 24px'
            }}>
              {[
                { id: 'overview', label: 'Overview', icon: 'information-circle-outline', testId: 'tax-sync-snapshot-tab-overview' },
                { id: 'changes', label: 'Changes', icon: 'git-compare-outline', testId: 'tax-sync-snapshot-tab-changes' },
                { id: 'errors', label: 'Errors & Warnings', icon: 'alert-circle-outline', testId: 'tax-sync-snapshot-tab-errors' },
                { id: 'raw', label: 'Raw Payload', icon: 'code-slash-outline', testId: 'tax-sync-snapshot-tab-raw' }
              ].map(tab => (
                <button
                  key={tab.id}
                  data-testid={tab.testId}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '12px 16px',
                    border: 'none',
                    background: 'transparent',
                    borderBottom: activeTab === tab.id ? `2px solid ${C.accent}` : '2px solid transparent',
                    color: activeTab === tab.id ? C.accent : C.textMid,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <Icon name={tab.icon} style={{ fontSize: 18 }} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
              {activeTab === 'overview' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Status</div>
                    <div>{statusBadge(snapshot.status)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Triggered By</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      {snapshot.triggeredBy === 'manual' ? 'Manual' : snapshot.triggeredBy === 'auto' ? 'Automated' : 'System'}
                    </div>
                    {snapshot.actorName && (
                      <div style={{ fontSize: 13, color: C.textMid }}>{snapshot.actorName}</div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Effective Date</div>
                    <div style={{ fontSize: 14, fontFamily: "'JetBrains Mono', monospace" }}>
                      {snapshot.effectiveDate}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Timestamp</div>
                    <div style={{ fontSize: 14, fontFamily: "'JetBrains Mono', monospace" }}>
                      {new Date(snapshot.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Source</div>
                    <div style={{ fontSize: 14 }}>{snapshot.source}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Duration</div>
                    <div style={{ fontSize: 14, fontFamily: "'JetBrains Mono', monospace" }}>
                      {snapshot.duration ? `${snapshot.duration}ms` : '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>Changes Summary</div>
                    <div style={{ fontSize: 14 }}>
                      +{snapshot.summary.added || 0} added, ↻{snapshot.summary.updated || 0} updated, −{snapshot.summary.removed || 0} removed
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.textMid, marginBottom: 4 }}>States Affected</div>
                    <div style={{ fontSize: 14 }}>{snapshot.summary.statesAffected || 0}</div>
                  </div>
                </div>
              )}

              {activeTab === 'changes' && (
                <div>
                  {snapshot.changes && snapshot.changes.length > 0 ? (
                    <div style={{
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      overflow: 'hidden'
                    }}>
                      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{
                            background: 'rgb(248, 250, 252)',
                            borderBottom: `1px solid ${C.border}`
                          }}>
                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12 }}>Jurisdiction</th>
                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12 }}>Tax Type</th>
                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12 }}>Change</th>
                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12 }}>Delta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {snapshot.changes.map((change, idx) => (
                            <tr key={idx} style={{
                              borderBottom: idx < snapshot.changes.length - 1 ? `1px solid ${C.border}` : 'none'
                            }}>
                              <td style={{ padding: '10px 12px', fontWeight: 600 }}>{change.jurisdiction}</td>
                              <td style={{ padding: '10px 12px' }}>{change.taxType}</td>
                              <td style={{ padding: '10px 12px' }}>{change.changeType}</td>
                              <td style={{ padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
                                {change.delta || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: 40, color: C.textMid }}>
                      No changes recorded
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'errors' && (
                <div>
                  {snapshot.errors && snapshot.errors.length > 0 ? (
                    <div style={{ marginBottom: 16 }}>
                      <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.danger }}>Errors</h4>
                      {snapshot.errors.map((error, idx) => (
                        <div key={idx} style={{
                          padding: 12,
                          borderRadius: 8,
                          background: 'rgb(254, 226, 226)',
                          border: '1px solid rgb(220, 38, 38)',
                          fontSize: 13,
                          color: 'rgb(120, 26, 26)',
                          marginBottom: 8
                        }}>
                          {error}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {snapshot.warnings && snapshot.warnings.length > 0 ? (
                    <div>
                      <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'rgb(217, 119, 6)' }}>Warnings</h4>
                      {snapshot.warnings.map((warning, idx) => (
                        <div key={idx} style={{
                          padding: 12,
                          borderRadius: 8,
                          background: 'rgb(254, 243, 199)',
                          border: '1px solid rgb(217, 119, 6)',
                          fontSize: 13,
                          color: 'rgb(120, 53, 15)',
                          marginBottom: 8
                        }}>
                          {warning}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {(!snapshot.errors || snapshot.errors.length === 0) && (!snapshot.warnings || snapshot.warnings.length === 0) && (
                    <div style={{ textAlign: 'center', padding: 40, color: C.textMid }}>
                      <Icon name="checkmark-circle" style={{ fontSize: 48, color: 'rgb(22, 163, 74)' }} />
                      <p style={{ marginTop: 12 }}>No errors or warnings</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'raw' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <button
                      data-testid="tax-sync-snapshot-raw-copy-button"
                      onClick={copyRawPayload}
                      style={{
                        ...S.buttonSecondary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <Icon name="copy-outline" style={{ fontSize: 16 }} />
                      Copy JSON
                    </button>
                  </div>
                  <pre style={{
                    padding: 16,
                    borderRadius: 10,
                    background: 'rgb(248, 250, 252)',
                    border: `1px solid ${C.border}`,
                    fontSize: 12,
                    fontFamily: "'JetBrains Mono', monospace",
                    overflow: 'auto',
                    maxHeight: '500px'
                  }}>
                    {JSON.stringify(snapshot.rawPayload, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {/* Footer actions for pending review snapshots */}
            {snapshot.status === 'pending_review' && (
              <div style={{
                padding: '16px 24px',
                borderTop: `1px solid ${C.border}`,
                background: 'rgb(248, 250, 252)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottomLeftRadius: 14,
                borderBottomRightRadius: 14
              }}>
                <div style={{ fontSize: 13, color: C.textMid, maxWidth: '60%' }}>
                  Review the changes and decide whether to apply or ignore this snapshot.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    data-testid="tax-sync-snapshot-ignore-button"
                    onClick={handleIgnore}
                    disabled={ignoring || applying}
                    style={{
                      ...S.buttonSecondary,
                      opacity: (ignoring || applying) ? 0.6 : 1,
                      cursor: (ignoring || applying) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {ignoring ? 'Ignoring...' : 'Ignore Changes'}
                  </button>
                  <button
                    data-testid="tax-sync-snapshot-apply-button"
                    onClick={handleApply}
                    disabled={applying || ignoring}
                    style={{
                      ...S.buttonPrimary,
                      opacity: (applying || ignoring) ? 0.6 : 1,
                      cursor: (applying || ignoring) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {applying ? 'Applying...' : 'Apply Changes'}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
